import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { trackToolViewed } from '@/lib/posthog';
import { TOOLS_BY_SLUG } from '../data';
import {
  SHARE_LINK_MAX_BYTES,
  shareablePdfByteLength,
  type ShareablePdfBytes,
} from './share-handoff';
import type { ToolSuccessVariant } from './tool-success-copy';
import {
  getToolSurfaceConfig,
  type ToolSurface,
  type ToolSurfaceConfig,
} from './tool-surface-config';

/**
 * A runner's single-PDF output, offered to the success panel's 'Get a share
 * link' step (W.4.124). `pdf` is null when the output is over the share-link
 * cap: the panel only needs the name and size to explain why, so the shell
 * does not keep large bytes alive.
 */
export interface OfferedShareablePdf {
  fileName: string;
  size: number;
  pdf: ShareablePdfBytes | null;
}

interface ToolShellContextValue {
  toolSlug: string;
  surface: ToolSurface;
  surfaceConfig: ToolSurfaceConfig;
  /**
   * Latches true on the first notifySuccess() and stays true for the life of a
   * tool page. Drives the inline success panel, which is a result state — there
   * is no dismissal and no suppression window to survive. It resets when the
   * visitor navigates to a sibling tool: the provider is not keyed by slug, so
   * without the reset Merge's result would show as "PDF compressed" on Compress.
   */
  hasSucceeded: boolean;
  successVariant?: ToolSuccessVariant;
  notifySuccess: () => void;
  notifySuccessVariant: (variant: ToolSuccessVariant) => void;
  /** Set by offerShareLink; resets with the latch on sibling-tool navigation. */
  shareablePdf: OfferedShareablePdf | null;
  /**
   * Offer the output for 'Get a share link'. Runner rule: call it with the
   * same bytes and file name the download used, immediately before
   * notifySuccess or notifySuccessVariant.
   */
  offerShareLink: (pdf: ShareablePdfBytes, fileName: string) => void;
  /**
   * Take the offer back once the runner's output is stale: a new run starts,
   * or the inputs or options change. The success latch stays.
   */
  withdrawShareLink: () => void;
}

/** What the current tool page produced; `generation` changes with the slug. */
interface ShellResult {
  generation: number;
  hasSucceeded: boolean;
  successVariant?: ToolSuccessVariant;
  shareablePdf: OfferedShareablePdf | null;
}

const EMPTY_RESULT: ShellResult = { generation: 0, hasSucceeded: false, shareablePdf: null };

const ToolShellContext = createContext<ToolShellContextValue | null>(null);

interface ProviderProps {
  toolSlug: string;
  surface?: ToolSurface;
  children: ReactNode;
}

export function ToolShellProvider({ toolSlug, surface = 'public', children }: ProviderProps) {
  const [result, setResult] = useState<ShellResult>(EMPTY_RESULT);
  // Reset the latch when the slug changes (sibling-tool navigation reuses this
  // provider). Adjusting state during render, not in an effect, so the new
  // tool's first paint never shows the previous tool's result.
  const [shellSlug, setShellSlug] = useState(toolSlug);
  if (shellSlug !== toolSlug) {
    setShellSlug(toolSlug);
    setResult((prev) => ({ ...EMPTY_RESULT, generation: prev.generation + 1 }));
  }
  const { generation, hasSucceeded, successVariant, shareablePdf } = result;
  // The callbacks belong to the tool page that handed them out. A runner whose
  // async work settles after the visitor moved to a sibling tool calls an
  // earlier generation's callback, and the update is dropped: otherwise the
  // next tool would latch, and offer the previous tool's file as its own.
  const notifySuccess = useCallback(() => {
    setResult((prev) =>
      prev.generation === generation
        ? { ...prev, hasSucceeded: true, successVariant: undefined }
        : prev,
    );
  }, [generation]);
  const notifySuccessVariant = useCallback(
    (variant: ToolSuccessVariant) => {
      setResult((prev) =>
        prev.generation === generation
          ? { ...prev, hasSucceeded: true, successVariant: variant }
          : prev,
      );
    },
    [generation],
  );
  const offerShareLink = useCallback(
    (pdf: ShareablePdfBytes, fileName: string) => {
      const size = shareablePdfByteLength(pdf);
      const kept = size > SHARE_LINK_MAX_BYTES ? null : pdf;
      setResult((prev) => {
        if (prev.generation !== generation) return prev;
        const current = prev.shareablePdf;
        // A repeat download of the same result (Compress) keeps the same offer,
        // so the panel's once-per-offer "shown" event does not fire again.
        if (
          current &&
          current.fileName === fileName &&
          current.size === size &&
          current.pdf === kept
        ) {
          return prev;
        }
        return { ...prev, shareablePdf: { fileName, size, pdf: kept } };
      });
    },
    [generation],
  );
  const withdrawShareLink = useCallback(() => {
    setResult((prev) =>
      prev.generation === generation && prev.shareablePdf !== null
        ? { ...prev, shareablePdf: null }
        : prev,
    );
  }, [generation]);
  const surfaceConfig = getToolSurfaceConfig(surface);

  // Fire tool_viewed once per tool page, shell-level, so EVERY tool reports the
  // top of the funnel — not just the image-to-pdf runner, which was previously
  // the only source (tool_viewed under-fired badly against tool_files_added).
  // Keyed on slug so navigating between sibling tools re-fires; the ref stops a
  // re-render from double-counting. `surface` keeps the public funnel sliceable
  // now that the in-app surface emits too.
  const viewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (viewedRef.current === toolSlug) return;
    viewedRef.current = toolSlug;
    trackToolViewed({
      tool: toolSlug,
      category: TOOLS_BY_SLUG[toolSlug]?.category ?? 'unknown',
      surface,
    });
  }, [toolSlug, surface]);

  const value = useMemo<ToolShellContextValue>(
    () => ({
      toolSlug,
      surface,
      surfaceConfig,
      hasSucceeded,
      successVariant,
      notifySuccess,
      notifySuccessVariant,
      shareablePdf,
      offerShareLink,
      withdrawShareLink,
    }),
    [
      toolSlug,
      surface,
      surfaceConfig,
      hasSucceeded,
      successVariant,
      notifySuccess,
      notifySuccessVariant,
      shareablePdf,
      offerShareLink,
      withdrawShareLink,
    ],
  );

  return <ToolShellContext.Provider value={value}>{children}</ToolShellContext.Provider>;
}

const NOOP = () => {};

export function useToolSuccess(): {
  notifySuccess: () => void;
  notifySuccessVariant: (variant: ToolSuccessVariant) => void;
  offerShareLink: (pdf: ShareablePdfBytes, fileName: string) => void;
  withdrawShareLink: () => void;
} {
  const ctx = useContext(ToolShellContext);
  if (!ctx) {
    return {
      notifySuccess: NOOP,
      notifySuccessVariant: NOOP,
      offerShareLink: NOOP,
      withdrawShareLink: NOOP,
    };
  }
  return {
    notifySuccess: ctx.notifySuccess,
    notifySuccessVariant: ctx.notifySuccessVariant,
    offerShareLink: ctx.offerShareLink,
    withdrawShareLink: ctx.withdrawShareLink,
  };
}

export function useToolShellContext(): ToolShellContextValue | null {
  return useContext(ToolShellContext);
}

export function useToolSurface(): ToolSurface {
  const ctx = useContext(ToolShellContext);
  return ctx?.surface ?? 'public';
}

export function useToolSurfaceConfig(): ToolSurfaceConfig {
  const ctx = useContext(ToolShellContext);
  return ctx?.surfaceConfig ?? getToolSurfaceConfig('public');
}
